/**
 * YearlyComparison Component
 * Used in: Dashboard screen - displays Year-over-Year Comparison (Monthly Averages)
 */
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FinancialData } from '../data/financialData';

interface YearlyComparisonProps {
  data: FinancialData[];
}

export const YearlyComparison: React.FC<YearlyComparisonProps> = ({ data }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Aggregate data by year
  const yearlyData = data.reduce((acc, item) => {
    const existing = acc.find(y => y.year === item.year);
    if (existing) {
      existing.totalIncome += item.totalIncome;
      existing.totalCOGS += item.totalCOGS;
      existing.grossProfit += item.grossProfit;
      existing.totalExpense += item.totalExpense;
      existing.netOrdinaryIncome += item.netOrdinaryIncome;
      existing.months += 1;
    } else {
      acc.push({
        year: item.year,
        totalIncome: item.totalIncome,
        totalCOGS: item.totalCOGS,
        grossProfit: item.grossProfit,
        totalExpense: item.totalExpense,
        netOrdinaryIncome: item.netOrdinaryIncome,
        months: 1
      });
    }
    return acc;
  }, [] as Array<{ 
    year: number; 
    totalIncome: number; 
    totalCOGS: number; 
    grossProfit: number; 
    totalExpense: number; 
    netOrdinaryIncome: number;
    months: number;
  }>);

  // Calculate averages for partial years
  const yearlyAverages = yearlyData.map(item => ({
    year: item.year.toString(),
    avgIncome: item.totalIncome / item.months,
    avgCOGS: item.totalCOGS / item.months,
    avgGrossProfit: item.grossProfit / item.months,
    avgExpense: item.totalExpense / item.months,
    avgNetIncome: item.netOrdinaryIncome / item.months,
    totalIncome: item.totalIncome,
    totalCOGS: item.totalCOGS,
    months: item.months
  }));


  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = yearlyAverages.find(d => d.year === label);
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label} ({data?.months} months)</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Year-over-Year Comparison (Monthly Averages)</h3>
      <div className="chart-container w-full overflow-x-auto">
        <ResponsiveContainer width="100%" height={450} minWidth={300}>
          <BarChart data={yearlyAverages} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="year" 
            stroke="#6b7280"
            fontSize={12}
            interval={0}
          />
          <YAxis 
            stroke="#6b7280"
            fontSize={12}
            tickFormatter={formatCurrency}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          
          <Bar 
            dataKey="avgIncome" 
            name="Avg Monthly Income"
            fill="#3b82f6"
            radius={[2, 2, 0, 0]}
          />
          <Bar 
            dataKey="avgCOGS" 
            name="Avg Monthly COGS"
            fill="#ef4444"
            radius={[2, 2, 0, 0]}
          />
          <Bar 
            dataKey="avgNetIncome" 
            name="Avg Monthly Net Income"
            fill="#8b5cf6"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};